/* eslint semi:0 */
/**
 * DOM abstractions to allow cross-brwoser and easy mocking.
 */
import bem from 'bem-cn'
import { getIsIe } from './ua'
import bemSelector from 'bem-selector'
import { warn } from '../application/console'
import { registerUtils } from '../application/nm'
import classnameGenerator from 'classname-generator'
import { create as makeEmptyObj } from './object'

bem.setup({
  mod: '--',
})

export function elementMatchesSelector(el, selector = null) {
  return (
    (selector &&
      el.nodeType === Node.ELEMENT_NODE &&
      (el.matches ||
        el.matchesSelector ||
        el.msMatchesSelector ||
        el.mozMatchesSelector ||
        el.webkitMatchesSelector ||
        el.oMatchesSelector)
        .call(el, selector)) ||
    false
  )
}

export function selectorMatchesElement(selector, el) {
  return elementMatchesSelector(el, selector)
}

export function makeImage(attrs) {
  return Object.assign(new Image(), attrs)
}

export const raf = (window.requestAnimationFrame ||
  window.mozRequestAnimationFrame ||
  window.webkitRequestAnimationFrame ||
  function fakeRaf(callback) {
    return callback()
  })
  .bind(window)

export const node = document.querySelector.bind(document)
export function insertBefore(newElement, targetElement) {
  const { parentNode } = targetElement
  parentNode.insertBefore(newElement, targetElement)
}

export function insertAfter(newElement, targetElement) {
  const { parentNode } = targetElement
  if (parentNode.lastchild === targetElement) {
    parentNode.appendChild(newElement)
  } else {
    parentNode.insertBefore(newElement, targetElement.nextSibling)
  }
}

export function nodeByTagName(tagName) {
  return document.getElementsByTagName(tagName)[0] || null
}

export function nodes(...args) {
  let compoundSelector = args.join(', ')
  if (!compoundSelector) {
    warn('No selectors passed to dom.nodes. Selectors:', args)
    return []
  }
  return document.querySelectorAll.call(document, compoundSelector)
}

export const liveNodesByClassName = document.getElementsByClassName.bind(document)

export const nodesByTagName = document.getElementsByTagName.bind(document)
export const createElement = document.createElement.bind(document)
export const addWindowEvent = window.addEventListener.bind(window)
export const addDocumentEvent = document.addEventListener.bind(document)
export const removeDocumentEvent = document.removeEventListener.bind(document)
export const getElementById = document.getElementById.bind(document)
export const getLayoutType = document.body.className
export function getSingleElementByClass(className) {
  return dom.liveNodesByClassName(className)[0] || null
}

export function changeClassName(selector, originalClass, newClass) {
  Array.prototype.slice
    .call(dom.liveNodesByClassName(selector))
    .forEach(function replaceClassName(element) {
      element.className = element.className.replace(originalClass, newClass)
    })
}

export function makeImagePromise(src, resolve, reject) {
  return new Promise(makeImage.part({ src, onerror: reject, onload: resolve }))
    .then(resolve)
    .catch(reject)
}

export function addElementAfter(newNode, referenceNode, skipOverSelector = null) {
  if (referenceNode && referenceNode.nextSibling) {
    let { parentNode, nextSibling: insertAfterNode } = referenceNode
    const shouldSkipEl = selectorMatchesElement.part(skipOverSelector)
    while (shouldSkipEl(insertAfterNode) || shouldSkipEl(insertAfterNode.nextSibling)) {
      insertAfterNode = insertAfterNode.nextSibling
    }
    parentNode.insertBefore(newNode, insertAfterNode)
    return newNode
  }
  return null
}

export function removeElement(node) {
  if (node.parentNode) {
    node.parentNode.removeChild(node)
  }
}

export function classList(node) {
  function classListPolyfillFn(classListFn) {
    return (...args) => {
      args.forEach(function(singleClass) {
        node.classList[classListFn](singleClass)
      })
    }
  }
  const classListPolyfill = getIsIe()
    ? {
        add: classListPolyfillFn('add'),
        remove: classListPolyfillFn('remove'),
        toggle: function toggleOriginalClassListFn(...args) {
          node.classList.toggle(...args)
        },
        contains: function containsOriginalClassListFn(...args) {
          node.classList.contains(...args)
        },
      }
    : node.classList
  return classListPolyfill
}
export { bem }

export { classnameGenerator }

export function toggleBemClass(bemFromSelector, bemToSelector) {
  Array.from(dom.nodes(`.${bemFromSelector}`)).forEach(function(node) {
    node.className = bemToSelector
  })
}

export function addClass(el, newClass) {
  el.className += ` ${newClass}`
}

export function removeClass(el, classToRemove) {
  let elClass = ` ${el.className} `
  while (elClass.includes(classToRemove)) {
    elClass = elClass.replace(classToRemove, '')
  }
  el.className = elClass.trim()
}

export function selectText(element) {
  let range
  if (document.body.createTextRange instanceof Function) {
    range = document.body.createTextRange()
    range.moveToElementText(element)
    range.select()
  } else if (window.getSelection instanceof Function) {
    const selection = window.getSelection()
    range = document.createRange()
    range.selectNodeContents(element)
    selection.removeAllRanges()
    selection.addRange(range)
  }
}

export function forceRedraw(el) {
  if (!(el instanceof Element)) {
    warn('Called forceRedraw with non Element:', el)
    return el
  }
  const { display } = el.style
  el.style.display = 'none'
  // reading this property forces webkit to reflow. Possible alternatives: http://stackoverflow.com/q/3485365
  el.offsetHeight
  el.style.display = display
  return el
}

export function updateClassName(selector, baseClass, modifiers) {
  const element = dom.node(selector)
  if (element instanceof Element) {
    const newClassName = bemSelector({
      name: baseClass,
      modifiers: modifiers,
    })
    element.className = newClassName
  }
}

export function isVisible(el) {
  if (!(el instanceof Element)) {
    warn('Called isVisible with non Element:', el)
    return false
  }
  return window.getComputedStyle(el).display !== 'none'
}

export function referringDomain() {
  const referrer = document.referrer.split('/')[2]
  return (referrer && referrer.match(document.domain)) || referrer === undefined ? '' : referrer
}

export function scrollPercent() {
  const h = document.documentElement
  const b = document.body
  const scrolled =
    h.scrollTop || b.scrollTop / ((h.scrollHeight || b.scrollHeight) - h.clientHeight) * 100
  return window.parseInt(scrolled, 10)
}

export function parentElementUntil(predicate) {
  return function checkElement(target) {
    if (!target || target === document.body) {
      return null
    }
    return predicate(target) ? target : checkElement(target.parentNode)
  }
}
const dom = Object.assign(makeEmptyObj(), {
  raf,
  node,
  insertAfter,
  nodeByTagName,
  nodes,
  liveNodesByClassName,
  nodesByTagName,
  createElement,
  addWindowEvent,
  addDocumentEvent,
  removeDocumentEvent,
  getElementById,
  getLayoutType,
  getSingleElementByClass,
  changeClassName,
  makeImage,
  makeImagePromise,
  elementMatchesSelector,
  selectorMatchesElement,
  addElementAfter,
  removeElement,
  classList,
  bem,
  classnameGenerator,
  toggleBemClass,
  addClass,
  removeClass,
  selectText,
  forceRedraw,
  updateClassName,
  isVisible,
  referringDomain,
  scrollPercent,
  parentElementUntil,
})

registerUtils({ dom })



// WEBPACK FOOTER //
// ./provolution/app/views/provolution/jsCore/utils/dom.js